import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDashboardViewModel } from '../viewmodels/DashboardViewModel';
import { Customer } from '../models/CustomerModels';
import { CustomerService } from '../services/CustomerService';
import { DashboardService } from '../services/DashboardService';
import type { DashboardKeywordListItem } from '../models/DashboardModels';
import { Payment } from '../models/PaymentModels';
import { PaymentService } from '../services/PaymentService';
import StatsCardMobile from '../components/StatsCardMobile';
import DashboardCustomerTableMobile from '../components/DashboardCustomerTableMobile';
import DashboardPaymentTableMobile from '../components/DashboardPaymentTableMobile';
import KeywordStatusSplitCardMobile from '../components/KeywordStatusSplitCardMobile';
import DashboardKeywordTableMobile from '../components/DashboardKeywordTableMobile';
import { useSelectedMonthPaymentStats } from '../hooks/useSelectedMonthPaymentStats';
import Calendar from '../components/Calendar';
import PageHeader from '../components/ui/PageHeader';
import {
  MainContent as DesktopMainContent,
  LoadingSpinner,
  ErrorMessage
} from '../styles/DashboardStyles';
import styled from 'styled-components';
import { colors, shadows, borderRadius } from '../styles/designTokens';
import WeeklyPaymentPointDeductionChart from '../components/WeeklyPaymentPointDeductionChart';
import PaginationMobile from '../components/PaginationMobile';
import { getDashboardWeekChartAnchorIso } from '../utils/dashboardWeekChartAnchor';

// 모바일 전용 MainContent 스타일 정의 - 밀도 있는 디자인
const MainContent = styled(DesktopMainContent)`
  margin-left: 0; // 데스크톱용 margin-left를 0으로 덮어쓰기
  padding: 12px 12px;
  background: ${colors.secondary[50]};

  @media (max-width: 768px) {
    padding: 8px 8px;
  }
`;

// 모바일 헤더 날짜 섹션 스타일 - 컴팩트
const MobileDateSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px 0;
  padding: 0 2px;
`;

// 모바일 날짜 라벨 스타일 - 작게
const MobileDateLabel = styled.span`
  font-weight: 600;
  color: ${colors.semantic.text.secondary};
  font-size: 12px;
  white-space: nowrap;
`;

// 모바일 날짜 입력 컨테이너 스타일
const MobileDateInputContainer = styled.div`
  flex: 1 1 260px;
  min-width: 240px;
`;


// 모바일 통계 그리드 - 더 밀도 있게
const MobileStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 12px;
`;

// 모바일 테이블 컨테이너 - 컴팩트
const MobileTableContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.lg};
  box-shadow: ${shadows.base};
  border: 1px solid ${colors.semantic.border.primary};
  margin-bottom: 12px;
  overflow: hidden;
`;

// 키워드 분포 섹션 스타일 - 초고밀도
const KeywordSection = styled.section`
  margin-bottom: 8px;
`;

const KeywordTitle = styled.h2`
  font-size: 13px;
  font-weight: 600;
  color: ${colors.semantic.text.secondary};
  margin: 0 0 6px 0;
  text-align: left;
  padding-left: 2px;
  line-height: 1.2;
`;

// 키워드 카드 컨테이너 - 2열 균등 배치
const KeywordCardsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  width: 100%;
`;

// 키워드 카드 - 균등 배치용 디자인
const KeywordCard = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.semantic.background.secondary} 0%,
    ${colors.semantic.background.primary} 100%
  );
  border-radius: ${borderRadius.lg};
  padding: 10px;
  border: 1px solid ${colors.semantic.border.primary};
  box-shadow: ${shadows.sm};
  position: relative;
  overflow: hidden;
  min-height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.primary[500]}, ${colors.primary[600]});
  }
`;

// 키워드 카드 헤더
const KeywordCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

// 키워드 타입 라벨
const KeywordTypeLabel = styled.span<{ type: 'auto' | 'manual' }>`
  font-size: 10px;
  font-weight: 600;
  color: ${props => (props.type === 'auto' ? colors.success[600] : colors.error[600])};
  background: ${props => (props.type === 'auto' ? colors.success[100] : colors.error[100])};
  padding: 2px 6px;
  border-radius: ${borderRadius.base};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

// 키워드 아이콘
const KeywordIcon = styled.div<{ type: 'auto' | 'manual' }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${props => (props.type === 'auto' ? colors.success[500] : colors.error[500])};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.semantic.text.inverse};
  font-size: 8px;
  font-weight: bold;
`;

// 키워드 메인 값
const KeywordMainValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${colors.secondary[800]};
  line-height: 1;
  margin-bottom: 2px;
`;

// 키워드 단위
const KeywordUnit = styled.span`
  font-size: 10px;
  color: ${colors.semantic.text.tertiary};
  font-weight: 500;
`;

// 키워드 서브 정보
const KeywordSubInfo = styled.div`
  font-size: 9px;
  color: ${colors.semantic.text.disabled};
  line-height: 1.2;
  margin-top: 2px;
`;

// 키워드 분포 미니 차트
const KeywordMiniChart = styled.div`
  height: 20px;
  margin-top: 4px;
  border-radius: 2px;
  background: linear-gradient(90deg, ${colors.neutral[200]} 0%, ${colors.neutral[100]} 100%);
  position: relative;
  overflow: hidden;
`;

const KeywordMiniBar = styled.div<{ percentage: number; color: string }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: ${props => Math.max(props.percentage, 5)}%;
  background: ${props => props.color};
  border-radius: 2px;
  transition: width 0.3s ease;
`;

const DeductionChartWrapper = styled.div`
  margin-bottom: 12px;
`;

function isoDateToYearMonth(iso: string): string | null {
  const m = iso.trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1].slice(0, 7) : null;
}

const CARD_KEYWORD_ACTIVE = '키워드 활성';
const CARD_KEYWORD_INACTIVE = '키워드 비활성';

const DASHBOARD_EMBED_PAGE_SIZE = 8;

type EmbedListPagination = {
  page: number;
  total: number;
  totalPages: number;
  limit: number;
};

function normalizeTotalPages(total: number, limit: number, apiTotalPages?: number): number {
  if (apiTotalPages != null && apiTotalPages > 0) {
    return apiTotalPages;
  }
  if (total <= 0) {
    return 0;
  }
  return Math.max(1, Math.ceil(total / Math.max(1, limit)));
}

// 모바일 대시보드 뷰 컴포넌트
const DashboardMobile: React.FC = () => {
  const [activeCard, setActiveCard] = useState<string | null>('현재 고객'); // 기본값을 '현재 고객'으로 설정

  // 고객 목록 상태
  const [customers, setCustomers] = useState<Customer[] | null>(null);
  const [customersLoading, setCustomersLoading] = useState<boolean>(false);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [customerPagination, setCustomerPagination] = useState<EmbedListPagination | null>(null);

  const [payments, setPayments] = useState<Payment[] | null>(null);
  const [paymentsLoading, setPaymentsLoading] = useState<boolean>(false);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPagination, setPaymentPagination] = useState<EmbedListPagination | null>(null);
  const [chartPaymentYearMonth, setChartPaymentYearMonth] = useState<string | null>(null);
  const [chartPaymentAnchorIso, setChartPaymentAnchorIso] = useState<string | null>(null);
  const [dashboardKeywords, setDashboardKeywords] = useState<DashboardKeywordListItem[] | null>(null);
  const [dashboardKeywordsLoading, setDashboardKeywordsLoading] = useState(false);
  const [dashboardKeywordsError, setDashboardKeywordsError] = useState<string | null>(null);
  const [keywordPagination, setKeywordPagination] = useState<EmbedListPagination | null>(null);

  // 선택된 날짜 상태 (기본값: 오늘 날짜)
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const {
    stats: monthPaymentStats,
    loading: monthPaymentLoading,
    error: monthPaymentError,
    monthTitleLabel
  } = useSelectedMonthPaymentStats(
    selectedDate,
    activeCard === '차트 결제' ? chartPaymentYearMonth : null
  );

  const weekChartAnchorIso = useMemo(
    () => getDashboardWeekChartAnchorIso(selectedDate, { weekStartsOn: 1 }),
    [selectedDate]
  );

  const chartHighlightedIsoDate =
    activeCard === '차트 결제'
      ? chartPaymentAnchorIso ?? weekChartAnchorIso
      : weekChartAnchorIso;

  // ViewModel 훅 사용
  const {
    dashboardData,
    loading,
    error,
    handleDateChange: vmHandleDateChange,
    getChangeIndicator,
    formatNumber
  } = useDashboardViewModel();

  // 디버깅용 로그
  useEffect(() => {
    if (dashboardData) {
      console.log('=== 모바일 차트 데이터 상세 확인 ===');
      console.log('keywordStats:', dashboardData.keywordStats);
      console.log('siteDistribution 전체:', dashboardData.siteDistribution);
      console.log('siteDistribution 각 항목:');
      dashboardData.siteDistribution.forEach((item, index) => {
        console.log(`  [${index}]:`, item);
      });
      console.log('자동필터결과:', dashboardData.siteDistribution.filter(item => item.site.includes('분 (자동)')));
      console.log('수동필터결과:', dashboardData.siteDistribution.filter(item => item.site.includes('분 (수동)')));
      console.log('================================');
    }
  }, [dashboardData]);

  // 날짜 변경 핸들러
  const handleDateChange = (date: Date | null) => {
    const toLocalISODateString = (d: Date) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    if (date) {
      setSelectedDate(date);
      // UTC 변환으로 날짜가 1일 틀어지는 것을 방지합니다.
      const dateString = toLocalISODateString(date);
      vmHandleDateChange(dateString);
      console.log('선택된 날짜:', dateString);
      const anchor = getDashboardWeekChartAnchorIso(date, { weekStartsOn: 1 });
      if (activeCard === '차트 결제') {
        setChartPaymentAnchorIso(anchor);
        const ym = isoDateToYearMonth(anchor);
        if (ym) setChartPaymentYearMonth(ym);
        setPaymentPage(1);
      }
      // 날짜 변경 시에는 카드 상태와 데이터를 유지 (사용자 경험 개선)
      // setActiveCard(null); // 제거: 날짜 변경 시에도 활성 카드 유지
      // setCustomers(null); // 제거: 날짜 변경 시에도 고객 테이블 유지
      // setCustomersError(null); // 제거: 에러 상태 유지
      // setPayments(null); // 제거: 날짜 변경 시에도 결제 테이블 유지
      // setPaymentsError(null); // 제거: 결제 에러 상태 유지
    } else {
      setSelectedDate(new Date());
      vmHandleDateChange(new Date().toISOString().split('T')[0]);
    }
  };

  const loadCustomerPage = useCallback(async (page: number) => {
    setCustomersLoading(true);
    setCustomersError(null);
    try {
      const response = await CustomerService.getCustomers({
        page,
        limit: DASHBOARD_EMBED_PAGE_SIZE
      });
      setCustomers(response.customers);
      const limit = response.limit || DASHBOARD_EMBED_PAGE_SIZE;
      setCustomerPagination({
        page: response.page,
        total: response.total,
        totalPages: normalizeTotalPages(response.total, limit, response.totalPages),
        limit
      });
    } catch (err: any) {
      console.error('📱 모바일 대시보드: 고객 목록 API 오류:', err);
      setCustomersError(err.message || '고객 목록을 불러오는데 실패했습니다.');
      setCustomers(null);
      setCustomerPagination(null);
    } finally {
      setCustomersLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    if (activeCard === '현재 고객' && customers !== null) {
      setCustomers(null);
      setCustomerPagination(null);
      setActiveCard(null);
      setDashboardKeywords(null);
      setDashboardKeywordsError(null);
      setKeywordPagination(null);
      return;
    }

    setActiveCard('현재 고객');
    setChartPaymentYearMonth(null);
    setChartPaymentAnchorIso(null);
    setPayments(null);
    setPaymentsError(null);
    setPaymentPagination(null);
    setPaymentPage(1);
    setDashboardKeywords(null);
    setDashboardKeywordsError(null);
    setKeywordPagination(null);
    setCustomers(null);
    setCustomerPagination(null);
    await loadCustomerPage(1);
  }, [activeCard, customers, loadCustomerPage]);

  const loadKeywordPage = useCallback(async (variant: 'active' | 'inactive', page: number) => {
    setDashboardKeywordsLoading(true);
    setDashboardKeywordsError(null);
    try {
      const res = await DashboardService.getDashboardKeywords({
        status: variant,
        page,
        limit: DASHBOARD_EMBED_PAGE_SIZE
      });
      setDashboardKeywords(res.items);
      const limit = res.limit || DASHBOARD_EMBED_PAGE_SIZE;
      const total = res.total;
      setKeywordPagination({
        page: res.page,
        total,
        totalPages: normalizeTotalPages(total, limit, res.total_pages),
        limit
      });
    } catch (err: any) {
      setDashboardKeywordsError(err.message || '키워드 목록을 불러오는데 실패했습니다.');
      setDashboardKeywords(null);
      setKeywordPagination(null);
    } finally {
      setDashboardKeywordsLoading(false);
    }
  }, []);

  const fetchDashboardKeywords = useCallback(
    async (variant: 'active' | 'inactive') => {
      const cardLabel = variant === 'active' ? CARD_KEYWORD_ACTIVE : CARD_KEYWORD_INACTIVE;
      if (activeCard === cardLabel && dashboardKeywords !== null) {
        setDashboardKeywords(null);
        setDashboardKeywordsError(null);
        setKeywordPagination(null);
        setActiveCard(null);
        return;
      }
      setActiveCard(cardLabel);
      setChartPaymentYearMonth(null);
      setChartPaymentAnchorIso(null);
      setCustomers(null);
      setCustomersError(null);
      setCustomerPagination(null);
      setPayments(null);
      setPaymentsError(null);
      setPaymentPagination(null);
      setPaymentPage(1);
      await loadKeywordPage(variant, 1);
    },
    [activeCard, dashboardKeywords, loadKeywordPage]
  );

  const handleChartDayClick = useCallback(
    (isoDate: string) => {
      const ym = isoDateToYearMonth(isoDate);
      if (!ym) return;
      const dayKey = isoDate.trim().match(/^(\d{4}-\d{2}-\d{2})/)?.[1] ?? '';
      if (
        activeCard === '차트 결제' &&
        chartPaymentYearMonth === ym &&
        chartPaymentAnchorIso === dayKey
      ) {
        setPayments(null);
        setPaymentsError(null);
        setPaymentPagination(null);
        setPaymentPage(1);
        setActiveCard(null);
        setChartPaymentYearMonth(null);
        setChartPaymentAnchorIso(null);
        setDashboardKeywords(null);
        setDashboardKeywordsError(null);
        setKeywordPagination(null);
        return;
      }
      if (activeCard === '차트 결제' && chartPaymentYearMonth === ym) {
        setChartPaymentAnchorIso(dayKey);
        return;
      }
      setActiveCard('차트 결제');
      setChartPaymentYearMonth(ym);
      setChartPaymentAnchorIso(dayKey);
      setPaymentPage(1);
      setCustomers(null);
      setCustomersError(null);
      setCustomerPagination(null);
      setDashboardKeywords(null);
      setDashboardKeywordsError(null);
      setKeywordPagination(null);
    },
    [activeCard, chartPaymentYearMonth, chartPaymentAnchorIso]
  );

  useEffect(() => {
    if (activeCard !== '차트 결제' || !chartPaymentYearMonth) return;
    let cancelled = false;
    setPaymentsLoading(true);
    setPaymentsError(null);
    setPayments(null);
    PaymentService.getPaymentStatsAndList({
      page: paymentPage,
      limit: DASHBOARD_EMBED_PAGE_SIZE,
      year_month: chartPaymentYearMonth
    })
      .then(res => {
        if (!cancelled) {
          setPayments(res.listResponse.payments);
          const lr = res.listResponse;
          const limit = lr.limit || DASHBOARD_EMBED_PAGE_SIZE;
          setPaymentPagination({
            page: lr.page,
            total: lr.total,
            totalPages: normalizeTotalPages(lr.total, limit, lr.totalPages),
            limit
          });
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setPaymentsError(err.message || '결제 목록을 불러오는데 실패했습니다.');
          setPaymentPagination(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPaymentsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [activeCard, chartPaymentYearMonth, paymentPage]);

  useEffect(() => {
    if (activeCard === '현재 고객' && customers === null && !customersLoading) {
      void loadCustomerPage(1);
    }
  }, [activeCard, customers, customersLoading, loadCustomerPage]);

  // 로딩 상태 처리
  if (loading) {
    return (
      <MainContent>
        <LoadingSpinner>
          <div className="spinner" />
        </LoadingSpinner>
      </MainContent>
    );
  }

  // 에러 상태 처리
  if (error) {
    return (
      <MainContent>
        <ErrorMessage>{error}</ErrorMessage>
      </MainContent>
    );
  }

  // 데이터가 없는 경우 처리
  if (!dashboardData) {
    return (
      <MainContent>
        <ErrorMessage>데이터를 불러올 수 없습니다.</ErrorMessage>
      </MainContent>
    );
  }

  const { stats, siteDistribution, weeklyDeduction } = dashboardData;
  // 요청 UI 기준: 상단 자동/수동 키워드 카드(분포 카드)는 표시하지 않음.
  const showKeywordCards = false;

  const keywordVariantForPagination: 'active' | 'inactive' | null =
    activeCard === CARD_KEYWORD_ACTIVE
      ? 'active'
      : activeCard === CARD_KEYWORD_INACTIVE
        ? 'inactive'
        : null;

  return (
    <MainContent>
      <PageHeader
        variant="compact"
        title="대시보드"
        description="주요 지표 및 최근 활동 개요"
      />

      {/* 모바일 날짜 선택 섹션 */}
      <MobileDateSection>
        <MobileDateLabel>조회 기준 주간:</MobileDateLabel>
        <MobileDateInputContainer>
          <Calendar
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            placeholder="yyyy.mm.dd ~ yyyy.mm.dd"
            maxDate={new Date()} // 오늘 이후 날짜 선택 불가
            mode="week"
            stepDays={7}
            weekStartsOn={1} // 월요일 시작
          />
        </MobileDateInputContainer>
      </MobileDateSection>

      {/* 키워드 분포 섹션 - 2열 균등 배치 (요청 UI에서는 숨김) */}
      {showKeywordCards && (
        <KeywordSection>
          <KeywordTitle>키워드 분포</KeywordTitle>
          <KeywordCardsContainer>
            {/* 자동 키워드 카드 */}
            <KeywordCard>
              <KeywordCardHeader>
                <KeywordTypeLabel type="auto">자동</KeywordTypeLabel>
                <KeywordIcon type="auto">A</KeywordIcon>
              </KeywordCardHeader>
              <KeywordMainValue>
                {formatNumber(dashboardData.keywordStats?.자동 || 0)}
                <KeywordUnit>건</KeywordUnit>
              </KeywordMainValue>
              <KeywordSubInfo>
                {siteDistribution.filter(item => item.site.includes('분 (자동)')).length > 0
                  ? `${siteDistribution.filter(item => item.site.includes('분 (자동)')).length}개 분류`
                  : '분류 없음'
                }
              </KeywordSubInfo>
              <KeywordMiniChart>
                <KeywordMiniBar
                  percentage={Math.min((dashboardData.keywordStats?.자동 || 0) / Math.max((dashboardData.keywordStats?.자동 || 0) + (dashboardData.keywordStats?.수동 || 0), 1) * 100, 100)}
                  color={colors.success[500]}
                />
              </KeywordMiniChart>
            </KeywordCard>

            {/* 수동 키워드 카드 */}
            <KeywordCard>
              <KeywordCardHeader>
                <KeywordTypeLabel type="manual">수동</KeywordTypeLabel>
                <KeywordIcon type="manual">M</KeywordIcon>
              </KeywordCardHeader>
              <KeywordMainValue>
                {formatNumber(dashboardData.keywordStats?.수동 || 0)}
                <KeywordUnit>건</KeywordUnit>
              </KeywordMainValue>
              <KeywordSubInfo>
                {siteDistribution.filter(item => item.site.includes('분 (수동)')).length > 0
                  ? `${siteDistribution.filter(item => item.site.includes('분 (수동)')).length}개 분류`
                  : '분류 없음'
                }
              </KeywordSubInfo>
              <KeywordMiniChart>
                <KeywordMiniBar
                  percentage={Math.min((dashboardData.keywordStats?.수동 || 0) / Math.max((dashboardData.keywordStats?.자동 || 0) + (dashboardData.keywordStats?.수동 || 0), 1) * 100, 100)}
                  color={colors.error[500]}
                />
              </KeywordMiniChart>
            </KeywordCard>
          </KeywordCardsContainer>
        </KeywordSection>
      )}

      {/* 주간 결제/포인트 차감 차트 */}
      {weeklyDeduction && (
        <DeductionChartWrapper>
          <WeeklyPaymentPointDeductionChart
            rangeLabel={weeklyDeduction.rangeLabel}
            previousPayments={weeklyDeduction.previousPayments}
            currentPayments={weeklyDeduction.currentPayments}
            previousPointDeductions={weeklyDeduction.previousPointDeductions}
            currentPointDeductions={weeklyDeduction.currentPointDeductions}
            monthlyCumulativePaymentsSeries={weeklyDeduction.monthlyCumulativePaymentsSeries}
            onChartDayClick={handleChartDayClick}
            highlightedIsoDate={chartHighlightedIsoDate}
          />
        </DeductionChartWrapper>
      )}

      {/* 모바일 통계 카드 2x2 그리드 */}
      <MobileStatsGrid>
        <StatsCardMobile
          title="현재 고객"
          value={stats.현재고객수}
          unit="명"
          change={getChangeIndicator(stats.금일가입자수)}
          formatNumber={formatNumber}
          clickable={true}
          onClick={fetchCustomers}
          isActive={activeCard === '현재 고객'}
        />
        <KeywordStatusSplitCardMobile
          variant="active"
          autoCount={stats.활성화키워드수_자동}
          manualCount={stats.활성화키워드수_수동}
          formatNumber={formatNumber}
          clickable
          isActive={activeCard === CARD_KEYWORD_ACTIVE}
          onClick={() => fetchDashboardKeywords('active')}
        />
        <KeywordStatusSplitCardMobile
          variant="inactive"
          autoCount={stats.비활성키워드수_자동}
          manualCount={stats.비활성키워드수_수동}
          formatNumber={formatNumber}
          clickable
          isActive={activeCard === CARD_KEYWORD_INACTIVE}
          onClick={() => fetchDashboardKeywords('inactive')}
        />
        <StatsCardMobile
          title={`${monthTitleLabel} 결제 금액`}
          value={monthPaymentLoading ? 0 : monthPaymentStats?.월결제금액 ?? 0}
          unit="원"
          metaItems={
            monthPaymentError
              ? undefined
              : !monthPaymentLoading && monthPaymentStats
                ? [{ label: '결제 건수', value: monthPaymentStats.월결제건수 }]
                : undefined
          }
          change={
            monthPaymentError
              ? { text: '통계 조회 실패', color: colors.error[600], icon: '●' }
              : monthPaymentStats
                ? getChangeIndicator(monthPaymentStats.증감금액)
                : undefined
          }
          formatNumber={formatNumber}
          isActive={activeCard === '차트 결제'}
        />
      </MobileStatsGrid>

      {/* 모바일 고객 테이블 표시 영역 */}
      {activeCard === '현재 고객' && (
        <MobileTableContainer>
          {customersLoading && <LoadingSpinner />}
          {customersError && <ErrorMessage>{customersError}</ErrorMessage>}
          {customers && !customersLoading && !customersError && (
            <DashboardCustomerTableMobile customers={customers} />
          )}
          {!customersLoading &&
            !customersError &&
            customerPagination &&
            customerPagination.total > 0 && (
              <PaginationMobile
                currentPage={customerPagination.page}
                totalPages={customerPagination.totalPages}
                total={customerPagination.total}
                limit={customerPagination.limit}
                onPageChange={page => void loadCustomerPage(page)}
              />
            )}
        </MobileTableContainer>
      )}

      {activeCard === '차트 결제' && (
        <MobileTableContainer>
          {paymentsLoading && <LoadingSpinner />}
          {paymentsError && <ErrorMessage>{paymentsError}</ErrorMessage>}
          {payments && !paymentsLoading && !paymentsError && (
            <DashboardPaymentTableMobile payments={payments} />
          )}
          {!paymentsLoading &&
            !paymentsError &&
            paymentPagination &&
            paymentPagination.total > 0 && (
              <PaginationMobile
                currentPage={paymentPagination.page}
                totalPages={paymentPagination.totalPages}
                total={paymentPagination.total}
                limit={paymentPagination.limit}
                onPageChange={setPaymentPage}
              />
            )}
        </MobileTableContainer>
      )}

      {(activeCard === CARD_KEYWORD_ACTIVE || activeCard === CARD_KEYWORD_INACTIVE) && (
        <MobileTableContainer>
          {dashboardKeywordsLoading && <LoadingSpinner />}
          {dashboardKeywordsError && <ErrorMessage>{dashboardKeywordsError}</ErrorMessage>}
          {activeCard === CARD_KEYWORD_ACTIVE &&
            dashboardKeywords &&
            !dashboardKeywordsLoading &&
            !dashboardKeywordsError && (
              <DashboardKeywordTableMobile items={dashboardKeywords} variant="active" />
            )}
          {activeCard === CARD_KEYWORD_INACTIVE &&
            dashboardKeywords &&
            !dashboardKeywordsLoading &&
            !dashboardKeywordsError && (
              <DashboardKeywordTableMobile items={dashboardKeywords} variant="inactive" />
            )}
          {keywordVariantForPagination &&
            !dashboardKeywordsLoading &&
            !dashboardKeywordsError &&
            keywordPagination &&
            keywordPagination.total > 0 && (
              <PaginationMobile
                currentPage={keywordPagination.page}
                totalPages={keywordPagination.totalPages}
                total={keywordPagination.total}
                limit={keywordPagination.limit}
                onPageChange={page => void loadKeywordPage(keywordVariantForPagination, page)}
              />
            )}
        </MobileTableContainer>
      )}

    </MainContent>
  );
};

export default DashboardMobile; 